<template>
  <div>
    <page-loading
      v-if="loading"
      message="Carregando vendas"
      subtitle="Organizando histórico e resumo financeiro"
    />
    <template v-else>
    <partner-premium-banner v-if="showPremiumAlert" class="mb-3" />

    <div class="sales-actions mb-4">
      <v-btn dark color="primary" depressed @click="handleNewSale">
        <v-icon left>mdi-plus</v-icon>
        Nova Venda
      </v-btn>
    </div>

    <page-toolbar class="mb-4">
      <template #filters>
        <v-dialog ref="dialog" v-model="dialog" persistent width="290px">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="formattedPeriod"
              label="Período"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              hide-details
              class="sales-filter-field br-form-field"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="selectedRange" range locale="pt-br">
            <v-btn @click="closeDialog" color="primary" text>Cancelar</v-btn>
            <v-btn @click="handleLoadSales" color="primary">Aplicar</v-btn>
          </v-date-picker>
        </v-dialog>
        <v-select
          :items="status"
          v-model="statusFiltered"
          item-text="title"
          item-value="value"
          label="Status"
          dense
          outlined
          hide-details
          class="sales-filter-field br-form-field"
        />
      </template>
      <template #actions>
        <v-btn dark color="primary" depressed @click="handleLoadSales">
          <v-icon left>mdi-filter</v-icon>
          Filtrar
        </v-btn>
      </template>
    </page-toolbar>

    <section class="page-summary mb-4">
      <h2 class="br-section-title">Resumo</h2>
      <v-row dense class="page-summary__grid">
        <v-col cols="12" sm="6" md="4" lg="2">
          <partner-summary-metric-card
            title="Total de vendas"
            :value="sales.length"
            icon="mdi-cart-outline"
            variant="network"
            tooltip="Quantidade de vendas listadas conforme o período e o status selecionados."
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
          <partner-summary-metric-card
            title="Pendentes"
            :value="handleGetSalesPending()"
            icon="mdi-clock-outline"
            variant="amber"
            tooltip="Vendas com status pendente no resultado atual."
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
          <partner-summary-metric-card
            title="Concluídas"
            :value="handleGetSalesConfirmed()"
            icon="mdi-check-circle-outline"
            variant="finance"
            tooltip="Vendas confirmadas no resultado atual."
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
          <partner-summary-metric-card
            title="Faturamento"
            :value="handleGrossSalesValue()"
            icon="mdi-cash-multiple"
            variant="finance"
            tooltip="Soma do valor total das vendas (bruto) no resultado filtrado."
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2">
          <partner-summary-metric-card
            title="Lucro (repasse)"
            :value="handleRealSalesValue()"
            icon="mdi-chart-line"
            variant="brand"
            tooltip="Soma do repasse ao parceiro nas vendas do resultado atual."
          />
        </v-col>
      </v-row>
    </section>
    <data-table-card
      title="Histórico de vendas"
      icon="mdi-cart-outline"
      :headers="headers"
      :items="sales"
      :items-per-page="10"
      :footer-props="{ 'items-per-page-text': 'vendas por página' }"
      :header-props="{ sortByText: 'Filtrar por' }"
      searchable
      :search.sync="search"
      search-label="Pesquise pelo nome do cliente, CPF, total da venda ou total de repasse"
      show-count
      no-data-text="Nenhuma venda encontrada"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-for="item in items" :key="`${item.source || 'online'}-${item.id}`">
            <td>{{ formatDate(item.createdAt) || "" }}</td>
            <td><status-badge-vue :status="item.status" /></td>
            <td>{{ item.customerName || "" }}</td>
            <td>{{ formatDocument(item.customerDocument || "") }}</td>
            <td>{{ formatPrice(item.value || "") }}</td>
            <td>{{ item.partnerTax }}%</td>
            <td>{{ formatPrice(item.instantCashbackValue || 0) }}</td>
            <td>{{ formatPrice(item.partnerValue || "") }}</td>
            <td class="text-center">
              <v-btn
                v-if="canShowQrCode(item)"
                icon
                small
                color="primary"
                title="Ver QR Code"
                :loading="qrDialog.loading && qrDialog.saleId === String(item.id)"
                @click="openQrCode(item)"
              >
                <v-icon>mdi-qrcode</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
      <template v-slot:no-data>
        <empty-state
          icon="mdi-cart-off"
          title="Nenhuma venda encontrada"
          description="Ajuste o período ou o status, ou registre uma nova venda."
          variant="muted"
        >
          <v-btn dark color="primary" depressed @click="handleNewSale">
            <v-icon left>mdi-plus</v-icon>
            Nova Venda
          </v-btn>
        </empty-state>
      </template>
    </data-table-card>

    <app-modal
      v-model="openDialogNotAccount"
      title="Não é possível continuar a venda!"
      icon="mdi-alert-circle-outline"
      :max-width="560"
      persistent
    >
      <p class="mb-0">
        Não identificamos sua conexão com o PagBank. Deseja conectar agora
        para seguir com as vendas?
      </p>
      <template #actions>
        <v-btn text @click="openDialogNotAccount = false">Não</v-btn>
        <v-btn dark color="primary" depressed @click="handleEditPartner()">Sim</v-btn>
      </template>
    </app-modal>

    <app-modal
      :value="qrDialog.visible"
      title="QR Code de pagamento"
      icon="mdi-qrcode-scan"
      :max-width="480"
      persistent
      show-close
      @input="onQrDialogToggle"
    >
      <transaction-qr-code-panel
        v-if="qrDialog.qrcodeImage"
        :key="`${qrDialog.source}-${qrDialog.saleId}-${qrDialog.transactionId}`"
        :customer-name="qrDialog.customerName"
        :qrcode-image="qrDialog.qrcodeImage"
        :qrcode-text="qrDialog.qrcodeText"
        :status="qrDialog.status"
      />
    </app-modal>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import StatusBadgeVue from "@/components/statusBadge/StatusBadge.vue";
import PartnerPremiumBanner from "@/components/premium/PartnerPremiumBanner.vue";
import PartnerSummaryMetricCard from "@/components/summary/PartnerSummaryMetricCard.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PageToolbar from "@/components/ui/PageToolbar.vue";
import DataTableCard from "@/components/ui/DataTableCard.vue";
import AppModal from "@/components/ui/AppModal.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import TransactionQrCodePanel from "@/components/sales/TransactionQrCodePanel.vue";

import { SaleType } from "@/types/sale/Sale";
import { PdvSaleType } from "@/types/pdv/Pdv";
import { PartnerDetailsType } from "@/types/partner/Partner";
import { GetAllSales } from "@/services/sales/GetAllSales";
import { GetTransactionStatus } from "@/services/sales/GetTransactionStatus";
import { pdvApi } from "@/services/pdv/pdvApi";
import { GetPartner } from "@/services/partners/GetPartner";
import { buildTransactionQRCodeText } from "@/utils/transactionQrcode";
import { format } from "date-fns";
import QRCode from "qrcode";

interface SortItem {
  name: string;
  phone: string;
  email: string;
  document: string;
}

export default defineComponent({
  components: {
    AppModal,
    DataTableCard,
    EmptyState,
    PageLoading,
    PageToolbar,
    PartnerPremiumBanner,
    PartnerSummaryMetricCard,
    StatusBadgeVue,
    TransactionQrCodePanel,
  },

  data() {
    return {
      mobileBreakpoint: 600,
      selectedRange: [] as string[],
      startDate: "",
      endDate: "",
      firstMoment: true,
      loading: true,
      showModal: false,
      showModalRemove: false,
      sales: [] as SaleType[],
      search: "",
      sortBy: [] as ReadonlyArray<SortItem>,
      sortDesc: false,
      headers: [
        {
          text: "Data e hora da compra",
          value: "createdAt",
        },
        {
          text: "Status",
          value: "status",
          align: "center",
        },
        { text: "Cliente", value: "customerName" },
        { text: "CPF", value: "customerDocument" },
        { text: "Total da venda", value: "value" },
        { text: "Taxa", value: "partnerTax" },
        { text: "Cashback Direto", value: "instantCashbackValue" },
        { text: "Total de repasse", value: "partnerValue" },
        { text: "", value: "actions", sortable: false, align: "center" },
      ],
      statusFiltered: "CONFIRMED",
      status: [
        {
          title: "Confirmada",
          value: "CONFIRMED",
        },
        {
          title: "Pendente",
          value: "PENDING",
        },
      ],
      partnerTax: 0,
      hasAccountNumber: false,
      idPartner: "",
      openDialogNotAccount: false,
      dialog: false,
      partnerInfo: null as PartnerDetailsType | null,
      qrDialog: {
        visible: false,
        loading: false,
        saleId: "",
        pdvSaleId: "",
        transactionId: "",
        customerName: "",
        qrcodeText: "",
        qrcodeImage: "",
        status: null as string | null,
        source: "" as "online" | "pdv" | "",
      },
      qrPollTimer: null as ReturnType<typeof setInterval> | null,
      openQrRequestId: 0,
    };
  },
  methods: {
    async handleLoadSales() {
      this.loading = true;
      const getAllSales = new GetAllSales();
      if (this.selectedRange.length > 0) {
        if (this.selectedRange[0] > this.selectedRange[1]) {
          this.startDate = this.selectedRange[1];
          this.endDate = this.selectedRange[0];
        } else {
          this.startDate = this.selectedRange[0];
          this.endDate = this.selectedRange[1];
        }
      }

      const [transactions, pdvResult] = await Promise.all([
        getAllSales.execute(this.startDate, this.endDate, this.statusFiltered),
        pdvApi.listSales({
          startDate: this.startDate || undefined,
          endDate: this.endDate || undefined,
          status: this.statusFiltered,
          limit: 200,
        }).catch(() => ({ data: [] as PdvSaleType[] })),
      ]);

      const fromTransactions: SaleType[] = (transactions || []).map((sale) => ({
        ...sale,
        brpointsValue: sale.brpointsValue || 0,
        transactionId: sale.id,
        qrPaymentValue: Number(sale.value),
        source: "online" as const,
      }));

      const fromPdv: SaleType[] = (pdvResult.data || []).map((sale: PdvSaleType) => {
        const qrPayment = this.resolvePdvQrPayment(sale);
        return {
          id: sale.id,
          value: Number(sale.totalAmount),
          brpointsValue: Number(sale.brpAmount || 0),
          customerName: sale.customer?.name || "",
          customerDocument: sale.customer?.document || "",
          status: sale.status as SaleType["status"],
          type: "PDV",
          partnerTax: this.partnerTax,
          instantCashbackValue: Number(sale.cashbackAmount || 0),
          partnerValue: Math.max(
            Number(sale.totalAmount) -
              (Number(sale.totalAmount) * this.partnerTax) / 100 -
              Number(sale.cashbackAmount || 0),
            0,
          ),
          createdAt: sale.createdAt,
          transactionId: qrPayment.transactionId,
          qrPaymentValue: qrPayment.amount,
          source: "pdv" as const,
        };
      });

      this.sales = [...fromTransactions, ...fromPdv].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

      this.loading = false;
    },
    async handleDetailPartner() {
      const getPartner = new GetPartner();
      const partner = await getPartner.execute();
      this.idPartner = partner.id || "";
      if (
        partner.digitalAccountNumber &&
        partner.digitalAccountNumber.length > 0
      ) {
        this.hasAccountNumber = true;
      } else {
        this.hasAccountNumber = false;
      }
      this.partnerTax = Number(partner.tax || 0);
      this.partnerInfo = partner;
    },
    resolvePdvQrPayment(sale: PdvSaleType) {
      const brpointsPayment = (sale.payments || []).find(
        (payment) => payment.paymentMethod === "BRPOINTS" && payment.transactionId,
      );
      return {
        transactionId: brpointsPayment?.transactionId || sale.transactionId,
        amount: brpointsPayment ? Number(brpointsPayment.amount) : undefined,
      };
    },
    async resolveQrPaymentData(sale: SaleType) {
      if (sale.source === "pdv") {
        const saleDetail = await pdvApi.getSale(sale.id);
        const qrPayment = this.resolvePdvQrPayment(saleDetail);
        return {
          transactionId: qrPayment.transactionId || sale.transactionId,
          amount: qrPayment.amount ?? sale.qrPaymentValue ?? sale.value,
          pdvSaleId: sale.id,
          customerName: saleDetail.customer?.name || sale.customerName,
        };
      }

      return {
        transactionId: sale.transactionId,
        amount: sale.qrPaymentValue ?? sale.value,
        pdvSaleId: "",
        customerName: sale.customerName,
      };
    },
    canShowQrCode(sale: SaleType): boolean {
      return sale.status === "PENDING" && !!sale.transactionId;
    },
    async openQrCode(sale: SaleType) {
      if (!this.canShowQrCode(sale)) return;

      const requestId = ++this.openQrRequestId;
      const selectedSaleId = String(sale.id);
      const selectedSource = sale.source || "online";

      this.stopQrPolling();
      this.qrDialog.loading = true;
      this.qrDialog.saleId = selectedSaleId;
      this.qrDialog.qrcodeImage = "";
      this.qrDialog.qrcodeText = "";
      this.qrDialog.status = null;

      try {
        const qrPayment = await this.resolveQrPaymentData(sale);
        if (requestId !== this.openQrRequestId) return;

        const transactionId = qrPayment.transactionId;
        if (!transactionId) {
          this.$store.dispatch("showSnack", {
            message: "Transação de pagamento não encontrada para esta venda",
            color: "warning",
            timer: 4000,
          });
          return;
        }

        const tx = await new GetTransactionStatus().execute(transactionId);
        if (requestId !== this.openQrRequestId) return;

        if (tx?.status !== "PENDING") {
          this.$store.dispatch("showSnack", {
            message: "Esta venda não está mais pendente",
            color: "warning",
            timer: 4000,
          });
          await this.handleLoadSales();
          return;
        }

        const partner = this.partnerInfo;
        const qrValue = Number(tx.value ?? qrPayment.amount ?? 0);
        const qrText = buildTransactionQRCodeText({
          transactionId: tx.id || transactionId,
          value: qrValue,
          pagbank: partner?.pagbankConnect,
          partner: {
            name: partner?.nickname || partner?.name || "",
            document: partner?.document || "",
          },
        });
        const qrcodeImage = await QRCode.toDataURL(qrText);
        if (requestId !== this.openQrRequestId) return;

        this.qrDialog = {
          visible: true,
          loading: false,
          saleId: selectedSaleId,
          pdvSaleId: qrPayment.pdvSaleId,
          transactionId: tx.id || transactionId,
          customerName: qrPayment.customerName,
          qrcodeText: qrText,
          qrcodeImage,
          status: null,
          source: selectedSource,
        };
        this.startQrPolling();
      } catch {
        if (requestId !== this.openQrRequestId) return;
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar QR Code",
          color: "error",
          timer: 4000,
        });
      } finally {
        if (requestId === this.openQrRequestId) {
          this.qrDialog.loading = false;
        }
      }
    },
    startQrPolling() {
      this.stopQrPolling();
      const getStatus = new GetTransactionStatus();
      this.qrPollTimer = setInterval(async () => {
        if (!this.qrDialog.transactionId) return;
        try {
          const tx = await getStatus.execute(this.qrDialog.transactionId);
          if (tx?.status === "CONFIRMED") {
            await this.handleQrConfirmed();
          }
        } catch { /* keep polling */ }
      }, 5000);
    },
    stopQrPolling() {
      if (this.qrPollTimer) {
        clearInterval(this.qrPollTimer);
        this.qrPollTimer = null;
      }
    },
    async handleQrConfirmed() {
      this.qrDialog.status = "CONFIRMED";
      this.stopQrPolling();
      if (this.qrDialog.source === "pdv" && this.qrDialog.pdvSaleId) {
        try {
          await pdvApi.confirmBrpoints(this.qrDialog.pdvSaleId);
        } catch { /* sale may already be confirmed */ }
      }
      this.$store.dispatch("showSnack", {
        message: "Pagamento confirmado",
        color: "success",
        timer: 4000,
      });
      setTimeout(async () => {
        this.closeQrDialog();
        await this.handleLoadSales();
      }, 2000);
    },
    closeQrDialog() {
      this.stopQrPolling();
      this.qrDialog.visible = false;
      this.qrDialog.qrcodeImage = "";
      this.qrDialog.qrcodeText = "";
      this.qrDialog.status = null;
      this.qrDialog.transactionId = "";
      this.qrDialog.pdvSaleId = "";
      this.qrDialog.saleId = "";
    },
    onQrDialogToggle(visible: boolean) {
      if (!visible) {
        this.closeQrDialog();
        return;
      }
      this.qrDialog.visible = true;
    },
    handleEditPartner() {
      this.openDialogNotAccount = false;
      this.$router.push({ path: "/my-data", query: { tab: "bank" } });
    },
    goToSettings() {
      this.$router.push({ name: "settings" });
    },
    handleNewSale() {
      if (!this.hasAccountNumber) {
        this.openDialogNotAccount = true;
        return;
      }
      this.$router.push("/pdv");
    },
    handleDetailSale(id: string) {
      this.$router.push(`/sale/form/${id}`);
    },
    phoneNumberFormat: (value: string): string => {
      if (!value) return "";
      const phone =
        "(" +
        value.substr(0, 2) +
        ") " +
        value.substr(2, 5) +
        "-" +
        value.substr(7, 8);
      return phone;
    },
    formatDate: (date: string): string => {
      const dateFormated = format(new Date(date), "dd/MM/yyyy HH:mm:ss");
      return dateFormated;
    },
    formatDocument: (document: string): string => {
      if (document.length === 11) {
        return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (document.length === 14) {
        return document.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5"
        );
      }
      return document;
    },
    formatPrice(value: number): string {
      if (!value && value !== 0) return "";
      const formattedValue = value.toFixed(2).replace(".", ",");
      return `R$ ${formattedValue}`;
    },
    handleGetSalesPending() {
      const salesPending = this.sales.filter(
        (sale) => sale.status === "PENDING"
      );
      return String(salesPending.length);
    },
    handleGetSalesConfirmed() {
      const salesPending = this.sales.filter(
        (sale) => sale.status === "CONFIRMED"
      );
      return String(salesPending.length);
    },
    handleGrossSalesValue() {
      const salesConfirmed = this.sales.filter(
        (sale) => sale.status === "CONFIRMED"
      );
      const grossSalesValue = salesConfirmed.reduce(
        (total, sale) => total + sale.value,
        0
      );
      return String(this.formatPrice(grossSalesValue));
    },
    handleRealSalesValue() {
      const salesConfirmed = this.sales.filter(
        (sale) => sale.status === "CONFIRMED"
      );
      const grossSalesValue = salesConfirmed.reduce(
        (total, sale) =>
          total +
          sale.value -
          (sale.value * sale.partnerTax) / 100 -
          sale.instantCashbackValue,
        0
      );
      return String(this.formatPrice(grossSalesValue));
    },
    handleInitialDaysInterval() {
      if (this.firstMoment) {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        const formattedToday = today.toISOString().split("T")[0];
        const formattedThirtyDaysAgo = thirtyDaysAgo
          .toISOString()
          .split("T")[0];
        this.selectedRange = [formattedToday, formattedThirtyDaysAgo];
        this.firstMoment = false;
      }
    },
    closeDialog() {
      this.dialog = false;
    },
    async submitDates() {
      this.dialog = false;
      this.loading = true;
      const getAllSalesListerner = new GetAllSales();
      const executeSales = await getAllSalesListerner.execute(
        this.startDate,
        this.endDate,
        this.statusFiltered
      );
      this.sales = executeSales;
      this.loading = false;
    },
    async handleHasAccountNumber() {
      const getPartner = new GetPartner();
      const partner = await getPartner.execute();
      this.idPartner = partner.id || "";
      if (
        partner.digitalAccountNumber &&
        partner.digitalAccountNumber.length > 0
      ) {
        this.hasAccountNumber = true;
      } else {
        this.hasAccountNumber = false;
      }
    },
  },
  mounted() {
    this.handleInitialDaysInterval();
    this.handleHasAccountNumber();
    this.handleDetailPartner();
    this.handleLoadSales();
  },
  beforeDestroy() {
    this.stopQrPolling();
  },
  computed: {
    formattedPeriod(): string {
      const parts = (this.selectedRange || [])
        .filter(Boolean)
        .map((date: string) => {
          const [year, month, day] = date.split("-");
          return `${day}/${month}/${year}`;
        });
      return parts.join(" — ") || "Selecione o período";
    },
    showPremiumAlert(): boolean {
      return !this.$store.state.sessionInfo.isPremium;
    },
  },
});
</script>

<style scoped lang="scss">
.sales-actions {
  display: flex;
  justify-content: flex-end;
}

.sales-filter-field {
  flex: 1 1 220px;
  min-width: 220px;
  max-width: 100%;
}

@media (max-width: 959px) {
  .sales-filter-field {
    flex: 1 1 100%;
    min-width: 100%;
  }
}
</style>
